import { rateSnapshotFor, canBeBillable, defaultBillable } from "./billing";
import { makeId } from "./clock";
import { PROJECT_COLORS, type CalendarEvent, type Project, type ProjectColor, type RepeatRule, type TimeEntry } from "./entities";
import { isLive, type Dataset, type OperationFactory, type SyncOperation } from "./operations";

const MINUTE = 60_000;

/* ---------------------------------------------------------------- local days */

const pad = (value: number) => String(value).padStart(2, "0");

/** "2026-10-22" for the local day containing `ts`. */
export function dayKey(ts: number): string {
  const date = new Date(ts);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Local midnight of a YYYY-MM-DD day. */
export function dayStartOf(key: string): number {
  const [year = 1970, month = 1, day = 1] = key.split("-").map(Number);
  return new Date(year, month - 1, day).getTime();
}

/** The same wall-clock time `days` local days later, correct across daylight saving changes. */
export function shiftDays(ts: number, days: number): number {
  const date = new Date(ts);
  date.setDate(date.getDate() + days);
  return date.getTime();
}

/** Whole local days from the day of `from` to the day of `to`. */
export function daysBetween(from: number, to: number): number {
  return Math.round((dayStartOf(dayKey(to)) - dayStartOf(dayKey(from))) / 86_400_000);
}

/* ---------------------------------------------------------------- project colour */

function hash(text: string): number {
  let value = 2166136261;
  for (let index = 0; index < text.length; index += 1) value = Math.imul(value ^ text.charCodeAt(index), 16777619);
  return value >>> 0;
}

/** A Project's colour: the one picked, or one derived from its id so it never changes. */
export function projectColor(project: Pick<Project, "id" | "color">): ProjectColor {
  return project.color ?? PROJECT_COLORS[hash(project.id) % PROJECT_COLORS.length]!;
}

/** The least used colour among active Projects, earliest in the palette on a tie. */
export function nextProjectColor(dataset: Dataset): ProjectColor {
  const used = new Map<ProjectColor, number>(PROJECT_COLORS.map((color) => [color, 0]));
  for (const project of Object.values(dataset.projects)) {
    if (isLive(project) && project.archivedAt === null) used.set(projectColor(project), (used.get(projectColor(project)) ?? 0) + 1);
  }
  return [...PROJECT_COLORS].sort((a, b) => used.get(a)! - used.get(b)!)[0]!;
}

/* ---------------------------------------------------------------- occurrences */

/** One appearance of an Event on the Calendar. */
export interface Occurrence {
  /** Unique on the Calendar: the series id, plus `@<original start>` when it repeats. */
  key: string;
  /** The Event whose fields this occurrence shows: the series itself or a replacement for one occurrence. */
  event: CalendarEvent;
  /** The repeating Event (or the Event itself) this occurrence belongs to. */
  seriesId: string;
  /** Original start of a repeating occurrence; null for single Events. Links and exceptions use it. */
  occurrence: number | null;
  /** Timed: real instants. All-day: local midnight of the first day and midnight after the last. */
  start: number;
  end: number;
  allDay: boolean;
  recurring: boolean;
}

/** Start and end of an Event as instants; all-day Events span whole local days. */
export function eventSpan(event: Pick<CalendarEvent, "allDay" | "start" | "end" | "startDay" | "endDay">): { start: number; end: number } {
  if (event.allDay && event.startDay && event.endDay) return { start: dayStartOf(event.startDay), end: shiftDays(dayStartOf(event.endDay), 1) };
  return { start: event.start, end: event.end };
}

const MAX_STEPS = 20_000;

/**
 * Original starts of a repeating series from `first`, in order, until `to` (exclusive) or the
 * rule ends. COUNT includes removed occurrences, as in RFC 5545.
 */
export function repeatStarts(first: number, rule: RepeatRule, to: number): number[] {
  const starts: number[] = [];
  const origin = new Date(first);
  const timeOf = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), origin.getHours(), origin.getMinutes(), origin.getSeconds()).getTime();
  const until = rule.until ? shiftDays(dayStartOf(rule.until), 1) : Infinity;
  let generated = 0;
  const accept = (ts: number): boolean => {
    if (ts < first) return true;
    if (ts >= until || ts >= to || (rule.count !== null && generated >= rule.count)) return false;
    generated += 1;
    starts.push(ts);
    return true;
  };

  if (rule.freq === "daily") {
    for (let step = 0; step < MAX_STEPS; step += 1) {
      if (!accept(timeOf(new Date(origin.getFullYear(), origin.getMonth(), origin.getDate() + step * rule.interval)))) break;
    }
  } else if (rule.freq === "weekly") {
    const weekdays = (rule.weekdays.length ? [...new Set(rule.weekdays)] : [origin.getDay()]).sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
    const monday = new Date(origin.getFullYear(), origin.getMonth(), origin.getDate() - ((origin.getDay() + 6) % 7));
    outer: for (let week = 0; week < MAX_STEPS; week += 1) {
      for (const weekday of weekdays) {
        const date = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + week * rule.interval * 7 + ((weekday + 6) % 7));
        if (!accept(timeOf(date))) break outer;
      }
    }
  } else {
    const day = origin.getDate();
    for (let step = 0; step < MAX_STEPS; step += 1) {
      const date = new Date(origin.getFullYear(), origin.getMonth() + step * rule.interval, 1);
      // Months without that day are skipped, never moved to their last day.
      if (new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate() < day) continue;
      if (!accept(timeOf(new Date(date.getFullYear(), date.getMonth(), day)))) break;
    }
  }
  return starts;
}

/** Every Event occurrence that overlaps [from, to), in start order. */
export function occurrencesBetween(dataset: Dataset, from: number, to: number): Occurrence[] {
  const result: Occurrence[] = [];
  // A replacement hides its generated occurrence even while in Trash; Undo brings it back.
  const replaced = new Set<string>();
  for (const event of Object.values(dataset.events)) {
    if (event.seriesId !== null && event.occurrenceStart !== null && event.discardedAt === null) replaced.add(`${event.seriesId}@${event.occurrenceStart}`);
  }
  const overlaps = (start: number, end: number) => start < to && (end > from || (end === start && start >= from));

  for (const event of Object.values(dataset.events)) {
    if (!isLive(event)) continue;
    const span = eventSpan(event);
    if (event.seriesId !== null) {
      const series = dataset.events[event.seriesId];
      if (!series || !isLive(series)) continue;
      if (overlaps(span.start, span.end)) result.push({ key: `${event.seriesId}@${event.occurrenceStart}`, event, seriesId: event.seriesId, occurrence: event.occurrenceStart, ...span, allDay: event.allDay, recurring: true });
      continue;
    }
    if (!event.repeat) {
      if (overlaps(span.start, span.end)) result.push({ key: event.id, event, seriesId: event.id, occurrence: null, ...span, allDay: event.allDay, recurring: false });
      continue;
    }
    const length = span.end - span.start;
    const lengthDays = event.allDay ? daysBetween(span.start, span.end) : 0;
    const excluded = new Set(event.exdates);
    for (const start of repeatStarts(span.start, event.repeat, to)) {
      const end = event.allDay ? shiftDays(start, lengthDays) : start + length;
      if (excluded.has(start) || replaced.has(`${event.id}@${start}`) || !overlaps(start, end)) continue;
      result.push({ key: `${event.id}@${start}`, event, seriesId: event.id, occurrence: start, start, end, allDay: event.allDay, recurring: true });
    }
  }
  return result.sort((a, b) => a.start - b.start || b.end - a.end || a.key.localeCompare(b.key));
}

/* ---------------------------------------------------------------- links to logged time */

/** Key that joins an occurrence to the Time Entries logged from it. */
export function linkKey(eventId: string, occurrence: number | null): string {
  return `${eventId}@${occurrence ?? ""}`;
}

/** Live Time Entries grouped by the Event occurrence they were logged from. */
export function loggedByOccurrence(dataset: Dataset): Map<string, TimeEntry[]> {
  const index = new Map<string, TimeEntry[]>();
  for (const entry of Object.values(dataset.timeEntries)) {
    if (!isLive(entry) || entry.eventId === null) continue;
    const key = linkKey(entry.eventId, entry.eventOccurrence);
    index.set(key, [...(index.get(key) ?? []), entry]);
  }
  return index;
}

/* ---------------------------------------------------------------- creating and changing Events */

export interface EventFields {
  title: string;
  notes: string;
  projectId: string | null;
  taskId: string | null;
  allDay: boolean;
  start: number;
  end: number;
  startDay: string | null;
  endDay: string | null;
  repeat: RepeatRule | null;
}

export function createEvent(fields: EventFields, factory: OperationFactory): { id: string; operations: SyncOperation[] } {
  const id = makeId("event");
  return { id, operations: [factory.op("event", id, { ...normalizeFields(fields), exdates: [], seriesId: null, occurrenceStart: null })] };
}

/** All-day Events keep `start`/`end` on their days too, so sorting and old readers stay sensible. */
function normalizeFields<T extends Partial<EventFields>>(fields: T): T {
  if (fields.allDay && fields.startDay && fields.endDay) return { ...fields, start: dayStartOf(fields.startDay), end: shiftDays(dayStartOf(fields.endDay), 1) };
  if (fields.allDay === false) return { ...fields, startDay: null, endDay: null };
  return fields;
}

export type EditScope = "this" | "following" | "all";

/** The fields of an occurrence as they appear, ready to copy into a replacement or a new series. */
function occurrenceFields(occurrence: Occurrence): EventFields {
  const { event } = occurrence;
  const lastDay = occurrence.allDay ? dayKey(occurrence.end - 1) : null;
  return {
    title: event.title, notes: event.notes, projectId: event.projectId, taskId: event.taskId, allDay: occurrence.allDay,
    start: occurrence.start, end: occurrence.end, startDay: occurrence.allDay ? dayKey(occurrence.start) : null, endDay: lastDay, repeat: null
  };
}

/** Occurrences of a series generated before `before`, for re-counting a COUNT rule when splitting it. */
function countBefore(series: CalendarEvent, before: number): number {
  if (!series.repeat) return 0;
  return repeatStarts(eventSpan(series).start, { ...series.repeat, count: series.repeat.count }, before).length;
}

/** Replacements of a series from `from` on, which a "this and following" change drops, as Google Calendar does. */
function replacementsFrom(dataset: Dataset, seriesId: string, from: number): CalendarEvent[] {
  return Object.values(dataset.events).filter((event) => isLive(event) && event.seriesId === seriesId && (event.occurrenceStart ?? 0) >= from);
}

/**
 * Changes one occurrence, it and the ones after it, or the whole series. Times in `patch` are the
 * occurrence's new times; for "all" the series moves by the same amount.
 */
export function editOccurrence(dataset: Dataset, occurrence: Occurrence, patch: Partial<EventFields>, scope: EditScope, factory: OperationFactory, now: number): SyncOperation[] {
  const normalized = normalizeFields({ allDay: occurrence.allDay, ...patch });
  if (!occurrence.recurring) return [factory.op("event", occurrence.event.id, normalized)];
  const series = dataset.events[occurrence.seriesId];
  if (!series || occurrence.occurrence === null) return [];

  if (scope === "this") {
    if (occurrence.event.seriesId !== null) return [factory.op("event", occurrence.event.id, normalized)];
    const id = makeId("event");
    return [factory.op("event", id, { ...normalizeFields({ ...occurrenceFields(occurrence), ...patch }), repeat: null, exdates: [], seriesId: series.id, occurrenceStart: occurrence.occurrence })];
  }

  const seriesSpan = eventSpan(series);
  if (scope === "following" && occurrence.occurrence > seriesSpan.start) {
    const rule = series.repeat!;
    const before = countBefore(series, occurrence.occurrence);
    const operations: SyncOperation[] = [factory.op("event", series.id, { repeat: rule.count !== null ? { ...rule, count: Math.max(1, before), until: null } : { ...rule, until: dayKey(shiftDays(occurrence.occurrence, -1)) } })];
    for (const replacement of replacementsFrom(dataset, series.id, occurrence.occurrence)) operations.push(factory.op("event", replacement.id, { deletedAt: now }));
    const fields = normalizeFields({ ...occurrenceFields({ ...occurrence, event: series }), ...patch });
    const id = makeId("event");
    operations.push(factory.op("event", id, {
      ...fields,
      repeat: patch.repeat !== undefined ? patch.repeat : { ...rule, count: rule.count !== null ? Math.max(1, rule.count - before) : null },
      exdates: series.exdates.filter((date) => date >= occurrence.occurrence!).map((date) => date + ((fields.start ?? occurrence.start) - occurrence.occurrence!)),
      seriesId: null,
      occurrenceStart: null
    }));
    return operations;
  }

  // The whole series: field changes apply as they are; time changes move every occurrence alike.
  const next: Record<string, unknown> = { ...normalized };
  delete next["start"]; delete next["end"]; delete next["startDay"]; delete next["endDay"]; delete next["allDay"];
  const allDay = patch.allDay ?? series.allDay;
  if (allDay) {
    const shift = patch.startDay ? daysBetween(occurrence.start, dayStartOf(patch.startDay)) : 0;
    const length = patch.startDay && patch.endDay ? daysBetween(dayStartOf(patch.startDay), dayStartOf(patch.endDay)) : daysBetween(seriesSpan.start, seriesSpan.end) - 1;
    const startDay = dayKey(shiftDays(seriesSpan.start, shift));
    Object.assign(next, normalizeFields({ allDay: true, startDay, endDay: dayKey(shiftDays(dayStartOf(startDay), Math.max(0, length))) }));
  } else if (patch.start !== undefined || patch.end !== undefined || patch.allDay === false) {
    const start = patch.start ?? occurrence.start;
    const end = patch.end ?? occurrence.end;
    const seriesStart = occurrence.allDay ? start : seriesSpan.start + (start - occurrence.start);
    Object.assign(next, { allDay: false, startDay: null, endDay: null, start: seriesStart, end: seriesStart + (end - start) });
  }
  return [factory.op("event", series.id, next)];
}

/** Removes one occurrence, it and the ones after it, or the whole series. */
export function deleteOccurrence(dataset: Dataset, occurrence: Occurrence, scope: EditScope, factory: OperationFactory, now: number): SyncOperation[] {
  if (!occurrence.recurring || occurrence.occurrence === null) return [factory.op("event", occurrence.event.id, { deletedAt: now })];
  const series = dataset.events[occurrence.seriesId];
  if (!series) return [];
  if (scope === "this") {
    const operations = [factory.op("event", series.id, { exdates: [...new Set([...series.exdates, occurrence.occurrence])] })];
    if (occurrence.event.seriesId !== null) operations.push(factory.op("event", occurrence.event.id, { deletedAt: now }));
    return operations;
  }
  if (scope === "following" && occurrence.occurrence > eventSpan(series).start) {
    const rule = series.repeat!;
    const operations = [factory.op("event", series.id, { repeat: rule.count !== null ? { ...rule, count: Math.max(1, countBefore(series, occurrence.occurrence)), until: null } : { ...rule, until: dayKey(shiftDays(occurrence.occurrence, -1)) } })];
    for (const replacement of replacementsFrom(dataset, series.id, occurrence.occurrence)) operations.push(factory.op("event", replacement.id, { deletedAt: now }));
    return operations;
  }
  return [factory.op("event", series.id, { deletedAt: now })];
}

/* ---------------------------------------------------------------- turning plans into time */

export interface LogSource {
  projectId: string | null;
  taskId: string | null;
  description: string;
  eventId: string;
  eventOccurrence: number | null;
  start: number;
  end: number;
}

export type LogResult = { operations: SyncOperation[]; logged: number } | { error: string };

/**
 * Logs a planned span as worked: clipped to now, and trimmed around time already tracked so
 * entries never overlap. Several entries are created when other time sits in the middle.
 */
export function logPlanned(dataset: Dataset, source: LogSource, factory: OperationFactory, now: number): LogResult {
  const end = Math.min(source.end, now);
  if (source.start >= now) return { error: "That is still ahead. Start a timer when it begins." };
  if (end - source.start < MINUTE) return { error: "There is no time to log yet." };
  const busy = Object.values(dataset.timeEntries)
    .filter((entry) => isLive(entry) && entry.start < end && (entry.end ?? now) > source.start)
    .map((entry) => [entry.start, entry.end ?? now] as const)
    .sort((a, b) => a[0] - b[0]);
  const gaps: Array<[number, number]> = [];
  let cursor = source.start;
  for (const [busyStart, busyEnd] of busy) {
    if (busyStart > cursor) gaps.push([cursor, Math.min(busyStart, end)]);
    cursor = Math.max(cursor, busyEnd);
    if (cursor >= end) break;
  }
  if (cursor < end) gaps.push([cursor, end]);
  const spans = gaps.filter(([start, stop]) => stop - start >= MINUTE);
  if (!spans.length) return { error: "That time is already tracked." };

  const project = source.projectId ? dataset.projects[source.projectId] : undefined;
  const billable = canBeBillable(project) && defaultBillable(project);
  const operations = spans.map(([start, stop]) => factory.op("timeEntry", makeId("entry"), {
    projectId: source.projectId,
    taskId: source.taskId,
    description: source.description,
    start,
    end: stop,
    billable,
    rateSnapshot: rateSnapshotFor(dataset, source.projectId, billable),
    invoiceId: null,
    eventId: source.eventId,
    eventOccurrence: source.eventOccurrence
  }));
  return { operations, logged: spans.reduce((sum, [start, stop]) => sum + stop - start, 0) };
}
